import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/molecules/Tabs';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Spinner } from '@/components/atoms/Spinner';
import { Badge } from '@/components/atoms/Badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/molecules/Dialog';
import { useAsyncData, useAsyncMutation } from '@/hooks/useAsyncData';
import { categoriesAPI } from '@/services/api/categories.api';
import type { BackofficeCategory, CategoryStatus } from '@/types/category.types';
import styles from './CategoryManagementPage.module.scss';

type TabKey = CategoryStatus | 'all';
const TABS: TabKey[] = ['active', 'pending', 'archived', 'all'];
const TAB_LABEL: Record<TabKey, string> = {
  active: 'Activas',
  pending: 'Pendientes de revisión',
  archived: 'Archivadas',
  all: 'Todas',
};
const SOURCE_LABEL: Record<BackofficeCategory['source'], string> = {
  seed: 'Inicial',
  ai: 'IA',
  admin: 'Manual',
};

interface DialogState {
  mode: 'create' | 'edit';
  category?: BackofficeCategory;
}

export function CategoryManagementPage() {
  const [tab, setTab] = useState<TabKey>('active');
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const list = useAsyncData<BackofficeCategory[]>({
    fetchFn: () => categoriesAPI.listAll(tab),
    dependencies: [tab],
  });

  const regenerate = useAsyncMutation({
    mutationFn: (id: string) => categoriesAPI.regenerateTranslation(id),
    onSuccess: () => void list.refetch(),
  });

  const remove = useAsyncMutation({
    mutationFn: (id: string) => categoriesAPI.remove(id),
    onSuccess: () => void list.refetch(),
  });

  const approve = useAsyncMutation({
    mutationFn: (id: string) => categoriesAPI.update(id, { status: 'active' }),
    onSuccess: () => void list.refetch(),
  });

  return (
    <div className={styles.page}>
      <div className={styles.page__header}>
        <div>
          <h1 className={styles.page__title}>Categorías</h1>
          <p className={styles.page__subtitle}>
            Catálogo de temas que el alumno puede seleccionar como intereses. Las que sugiere la
            IA entran como "Pendientes" hasta que un admin las aprueba.
          </p>
        </div>
        <Button variant="primary" onClick={() => setDialog({ mode: 'create' })}>
          Nueva categoría
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          {TABS.map((k) => (
            <TabsTrigger key={k} value={k}>
              {TAB_LABEL[k]}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((k) => (
          <TabsContent key={k} value={k}>
            <Card>
              <CardHeader>
                <CardTitle>{TAB_LABEL[k]}</CardTitle>
              </CardHeader>
              <CardContent>
                {list.isLoading ? (
                  <div className={styles.page__loading}>
                    <Spinner size="lg" aria-label="Cargando" />
                  </div>
                ) : !list.data || list.data.length === 0 ? (
                  <div className={styles.page__empty}>
                    <p>No hay categorías en este filtro.</p>
                  </div>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Nombre (EN)</th>
                        <th>Nombre (ES)</th>
                        <th>Slug</th>
                        <th>Origen</th>
                        <th>Stories</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {list.data.map((cat) => (
                        <tr key={cat.id}>
                          <td>{cat.name}</td>
                          <td>
                            {cat.nameTranslation ?? (
                              <span className={styles.table__warning}>Falta traducción</span>
                            )}
                          </td>
                          <td>
                            <code>{cat.slug}</code>
                          </td>
                          <td>
                            <Badge variant="secondary">{SOURCE_LABEL[cat.source]}</Badge>
                          </td>
                          <td>{cat.storiesCount}</td>
                          <td>
                            <div className={styles.table__actions}>
                              {cat.status === 'pending' && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  isLoading={approve.isLoading}
                                  onClick={() => void approve.mutate(cat.id)}
                                >
                                  Aprobar
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDialog({ mode: 'edit', category: cat })}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                isLoading={regenerate.isLoading}
                                onClick={() => void regenerate.mutate(cat.id)}
                              >
                                Regenerar traducción
                              </Button>
                              {cat.status !== 'archived' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  isLoading={remove.isLoading}
                                  onClick={() => void remove.mutate(cat.id)}
                                >
                                  Archivar
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {dialog && (
        <CategoryEditDialog
          state={dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void list.refetch();
          }}
        />
      )}
    </div>
  );
}

interface CategoryEditDialogProps {
  state: DialogState;
  onClose: () => void;
  onSaved: () => void;
}

function CategoryEditDialog({ state, onClose, onSaved }: CategoryEditDialogProps) {
  const cat = state.category;
  const [name, setName] = useState(cat?.name ?? '');
  const [nameTranslation, setNameTranslation] = useState(cat?.nameTranslation ?? '');
  const [slug, setSlug] = useState(cat?.slug ?? '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setName(cat?.name ?? '');
    setNameTranslation(cat?.nameTranslation ?? '');
    setSlug(cat?.slug ?? '');
    setErrorMessage(null);
  }, [cat]);

  const save = useAsyncMutation({
    mutationFn: async () => {
      if (state.mode === 'create') {
        return categoriesAPI.create({
          name: name.trim(),
          nameTranslation: nameTranslation.trim() || undefined,
          slug: slug.trim() || undefined,
        });
      }
      if (!cat) throw new Error('Missing category');
      return categoriesAPI.update(cat.id, {
        name: name.trim() || undefined,
        nameTranslation: nameTranslation.trim() || null,
      });
    },
    onSuccess: () => onSaved(),
    onError: (e) => setErrorMessage(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!name.trim()) {
      setErrorMessage('El nombre en inglés es obligatorio.');
      return;
    }
    void save.mutate(undefined as never);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {state.mode === 'create' ? 'Crear categoría' : 'Editar categoría'}
          </DialogTitle>
        </DialogHeader>
        <form className={styles.dialog__form} onSubmit={handleSubmit}>
          <div className={styles.dialog__row}>
            <label htmlFor="cat-name">Nombre en inglés</label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="cat-name-es">Nombre en español</label>
            <Input
              id="cat-name-es"
              value={nameTranslation}
              onChange={(e) => setNameTranslation(e.target.value)}
              placeholder="(opcional, se regenera con IA si lo dejas en blanco)"
            />
          </div>
          {state.mode === 'create' && (
            <div className={styles.dialog__row}>
              <label htmlFor="cat-slug">Slug</label>
              <Input
                id="cat-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="(se deriva del nombre si lo dejas vacío)"
              />
            </div>
          )}
          {errorMessage && <p className={styles.dialog__error}>{errorMessage}</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={save.isLoading}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CategoryManagementPage;
